import { Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';

import { VirtualCompetitionPrefix } from '@frontend/sports/common/core/data-access/sport-model';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { isArray } from 'lodash-es';
import { Observable, tap } from 'rxjs';

import { PreviousPageLocationCacheService } from '../../../client-caching/previous-page-cache.service';
import { CompetitionListSeoService } from '../../../competition-list/competition-list-seo.service';
import { FixtureList } from '../../../competition-list/competition.models';
import { CompetitionOverview } from '../../../competition-list/services/competition-list.service';
import { CompetitionService } from '../../../competition-list/services/competition.service';
import { ChildDependencyResolver } from '../../../dependency/child-dependency.resolver';
import { MasterDependencyResolver } from '../../../dependency/master-dependency.resolver';

@Injectable({ providedIn: 'root' })
export class CompetitionListResolver extends MasterDependencyResolver<CompetitionOverview, FixtureList> {
    constructor(
        dependencyService: CompetitionService,
        router: Router,
        private urlHelper: UrlHelperService,
    ) {
        super(dependencyService, router);
    }

    getChildRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, result?: CompetitionOverview): string {
        const service = this.dependencyService as CompetitionService;
        const target = service.getTarget(result);
        const leaguePrefix = target.isVirtual ? VirtualCompetitionPrefix : undefined;

        if (!target.sport) {
            return state.url;
        }

        return this.urlHelper.getSportUrl(
            this.getUrlParameter(target.sport, target.sportName),
            target.context,
            this.getUrlParameter(target.region, target.regionName),
            this.getUrlParameter(target.league, target.leagueName, leaguePrefix),
        );
    }

    private getUrlParameter(id?: number[] | number, name?: string, prefix?: string): UrlParam | undefined {
        if (!(id && !isArray(id) && name)) {
            return;
        }

        const urlParameter: UrlParam = { id, name };
        if (prefix) {
            urlParameter.prefix = prefix;
        }

        return urlParameter;
    }
}

@Injectable({ providedIn: 'root' })
export class CompetitionResolver extends ChildDependencyResolver<CompetitionOverview, FixtureList> {
    constructor(
        dependencyService: CompetitionService,
        private competitionListSeoService: CompetitionListSeoService,
        @Optional() previousPageLocationCacheService: PreviousPageLocationCacheService,
    ) {
        super(dependencyService, previousPageLocationCacheService);
    }

    override resolve(route: ActivatedRouteSnapshot): Observable<FixtureList | undefined> {
        return super.resolve(route).pipe(tap((fixtureList) => this.competitionListSeoService.storeRealCompetitionId(fixtureList)));
    }
}
